import React, { useState, useEffect, useRef, useContext } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Button,
  Breadcrumbs,
  Link,
  Typography,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Modal,
  Box,
  Snackbar,
  Skeleton,
  Stack,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShareIcon from "@mui/icons-material/Share";
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios, { CancelTokenSource } from "axios";
import { saveAs } from "file-saver";
import { useAuth0 } from "react-auth0-spa";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { useNavigate } from "react-router-dom";
import {
  apiRequest,
  ApiCalls,
} from "../../forms/providerAppointmentForms/AllAppointmentsApi";
import { myContext } from "../../../hooks/useContext/useContext";


interface Client {
  id: number;
  name: string;
}

interface Document {
  id: string;
  name: string;
  type: string;
  url?: string;
  date?: string;
  noteId?: number;
  eventId?: number;
  clientName?: string;
  clientId?: number;
}

interface SignedUrlResponse {
  key: string;
  url: string;
  lastModified: string;
  folder: string;
  size: number;
}

interface DocumentViewerProps {
  documents: Document[];
  clients: Client[];
  loading: boolean;
  error: boolean;
  isAllDocuments?: boolean;
  showBreadcrumbs?: boolean;
  maxDisplayDocuments?: number;
  onSeeAllDocuments?: () => void;
  clientEmail?: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documents,
  clients,
  loading,
  error,
  isAllDocuments = false,
  showBreadcrumbs = false,
  maxDisplayDocuments,
  onSeeAllDocuments,
  clientEmail: _clientEmail,
}) => {
  const { getTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { setPdfViewerOpen: setGlobalPdfViewerOpen } = useContext(myContext);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("");
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [pdfFileName, setPdfFileName] = useState<string>("");
  const [_downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'viewing' | 'sharing' | 'completed' | 'error' | 'cancelled'>('idle');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [downloadedFileUrl, setDownloadedFileUrl] = useState<string>("");
  const [lastFailedOperation, setLastFailedOperation] = useState<'download' | 'share' | 'view' | null>(null);
  const [lastFailedDocument, setLastFailedDocument] = useState<Document | null>(null);
  const [failedDocuments, setFailedDocuments] = useState<Set<string>>(new Set());
  const [showingFailureNotification, setShowingFailureNotification] = useState<boolean>(false);
  const [failureNotification, setFailureNotification] = useState<{
    show: boolean;
    document: Document | null;
    timeoutId: NodeJS.Timeout | null;
    progress: number;
    progressIntervalId: NodeJS.Timeout | null;
    message: string;
    isDeepStorage: boolean;
  }>({ show: false, document: null, timeoutId: null, progress: 100, progressIntervalId: null, message: "", isDeepStorage: false });
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  // Effect to clear document type filter when it's not available for selected client
  useEffect(() => {
    if (documentTypeFilter && selectedClientId !== null) {
      const availableTypes = [...new Set(
        documents
          .filter((doc) => doc.clientId === selectedClientId)
          .map((doc) => doc.name)
      )];
      
      if (!availableTypes.includes(documentTypeFilter)) {
        setDocumentTypeFilter("");
      }
    }
  }, [selectedClientId, documents, documentTypeFilter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (failureNotification.timeoutId) {
        clearTimeout(failureNotification.timeoutId);
      }
      if (failureNotification.progressIntervalId) {
        clearInterval(failureNotification.progressIntervalId);
      }
    };
  }, [failureNotification.timeoutId, failureNotification.progressIntervalId]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    document: Document
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedDocument(document);
  };

  const showFailureNotification = (document: Document, message: string = "Document Failed to Download", isDeepStorage: boolean = false) => {
    // Clear any existing timeouts
    if (failureNotification.timeoutId) {
      clearTimeout(failureNotification.timeoutId);
    }
    if (failureNotification.progressIntervalId) {
      clearInterval(failureNotification.progressIntervalId);
    }

    // Hide snackbar when showing failure notification
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setShowingFailureNotification(true);

    // Set up progress countdown (update every 300ms for smooth animation)
    let progress = 100;
    const progressIntervalId = setInterval(() => {
      progress -= (100 / (30000 / 300)); // Decrease by percentage every 300ms
      if (progress <= 0) {
        clearInterval(progressIntervalId);
        setFailureNotification({ show: false, document: null, timeoutId: null, progress: 100, progressIntervalId: null, message: "", isDeepStorage: false });
        setShowingFailureNotification(false);
        return;
      }
      setFailureNotification(prev => ({ ...prev, progress }));
    }, 300);

    // Set up auto-close after 30 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(progressIntervalId);
      setFailureNotification({ show: false, document: null, timeoutId: null, progress: 100, progressIntervalId: null, message: "", isDeepStorage: false });
      setShowingFailureNotification(false);
    }, 30000);

    setFailureNotification({
      show: true,
      document,
      timeoutId,
      progress: 100,
      progressIntervalId,
      message,
      isDeepStorage
    });
  };

  const hideFailureNotification = () => {
    if (failureNotification.timeoutId) {
      clearTimeout(failureNotification.timeoutId);
    }
    if (failureNotification.progressIntervalId) {
      clearInterval(failureNotification.progressIntervalId);
    }
    
    // Clear any pending snackbar messages when hiding the failure notification
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setShowingFailureNotification(false);
    
    setFailureNotification({ show: false, document: null, timeoutId: null, progress: 100, progressIntervalId: null, message: "", isDeepStorage: false });
  };

  const handleNotificationRetry = () => {
    if (failureNotification.document) {
      handleFailedDocumentClick(failureNotification.document);
      hideFailureNotification();
    }
  };

  const handleFailedDocumentClick = async (document: Document) => {
    // Hide any existing failure notification
    hideFailureNotification();
    
    // Remove from failed documents and try to fetch again
    setFailedDocuments(prev => {
      const newSet = new Set(prev);
      newSet.delete(document.id);
      return newSet;
    });

    // Try to fetch the signed URL again
    const signedUrlData = await fetchSignedUrl(document);
    if (!signedUrlData) {
      // If it fails again, it will be re-added to failedDocuments by fetchSignedUrl
      setSnackbarMessage("Document still unavailable");
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 2000);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocument(null);
  };

  const fetchSignedUrl = async (document: Document): Promise<SignedUrlResponse | null> => {
    console.log(document, 'document')
    try {
      const accessToken = await getTokenSilently();
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      console.log(config)

      // Uncomment this for real API call:
      const response = await axios.post(
        `${apiRequest}/${ApiCalls.GetSignedUrlsForBloomDocuments}`,
        { noteId: document.noteId },
        config
      );

      console.log("[DocumentViewer] fetchSignedUrl response:", response.data);

      // Handle error response with deep storage message
      if (response.data?.errors && Array.isArray(response.data.errors)) {
        const deepStorageError = response.data.errors.find((error: any) => 
          error.extensions?.code === "202" || 
          error.message?.includes("deep storage") ||
          error.message?.includes("not been accessed in more than 90 days")
        );
        
        if (deepStorageError) {
          console.log("[DocumentViewer] Deep storage error for document:", document.id);
          // DON'T mark this document as failed for deep storage errors
          // setFailedDocuments(prev => new Set(prev).add(document.id));
          // Show failure notification with dynamic message and deep storage flag
          showFailureNotification(document, "This file hasn’t been accessed in a while, so it’s stored in our archive. We’re retrieving it now. Please check back after 30 mins to view the file.", true);
          return null;
        }
      }

      // Handle the new response format: { "getLatestPDFs": [] }
      if (response.data !== undefined) {
        if (Array.isArray(response.data) && response.data.length === 0) {
          console.log("[DocumentViewer] Empty getLatestPDFs array for document:", document.id);
          // Mark this document as failed
          setFailedDocuments(prev => new Set(prev).add(document.id));
          return null;
        }
        // If not empty, return the first item (future implementation)
        if (Array.isArray(response.data.getLatestPDFs) && response.data.getLatestPDFs.length > 0) {
          return response.data.getLatestPDFs[0] as SignedUrlResponse;
        }
      }

      // Handle original response format
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0] as SignedUrlResponse;
      }
      
      return null;
    } catch (err) {
      console.error("[DocumentViewer] Error fetching signed URL:", err);
      
      // Check if the error response contains the deep storage message
      if (err && typeof err === 'object' && 'response' in err) {
        const errorResponse = (err as any).response;
        if (errorResponse?.data?.errors && Array.isArray(errorResponse.data.errors)) {
          const deepStorageError = errorResponse.data.errors.find((error: any) => 
            error.extensions?.code === "202" || 
            error.message?.includes("deep storage") ||
            error.message?.includes("not been accessed in more than 90 days")
          );
          
          if (deepStorageError) {
            console.log("[DocumentViewer] Deep storage error in catch for document:", document.id);
            // Mark this document as failed
            setFailedDocuments(prev => new Set(prev).add(document.id));
            // Show failure notification with dynamic message
            showFailureNotification(document, deepStorageError.message, true);
            return null;
          }
        }
      }
      
      return null;
    }
  };

  const handleCancelDownload = () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Download cancelled by user');
      cancelTokenRef.current = null;
    }
    setDownloadStatus('cancelled');
    setSnackbarMessage("Download cancelled");
    setActionLoading(false);
    setDownloadProgress(0);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  const handleOpenDownloadedFile = () => {
    if (downloadedFileUrl) {
      window.open(downloadedFileUrl, '_blank');
    }
    setSnackbarOpen(false);
  };

  const handleDownload = async (directUrl?: string, directFileName?: string, document?: Document) => {
    // Determine which document to use
    const targetDocument = document || selectedDocument;
    
    if (!targetDocument && !directUrl) {
      handleMenuClose();
      return;
    }

    handleMenuClose();
    setActionLoading(true);
    setDownloadStatus('downloading');
    setDownloadProgress(0);
    setDownloadedFileUrl("");
    setSnackbarMessage("Document Downloading...");
    setSnackbarOpen(true);

    try {
      let signedUrl: string;
      let fileName: string;

      // If direct URL provided (from PDF viewer), use it
      if (directUrl && directFileName) {
        signedUrl = directUrl;
        fileName = directFileName;
      } else if (targetDocument) {
        // Fetch signed URL for menu-based downloads
        cancelTokenRef.current = axios.CancelToken.source();
        
        const signedUrlData = await fetchSignedUrl(targetDocument);
        if (!signedUrlData?.url) {
          setDownloadStatus('error');
          // Only show snackbar if we're not already showing a failure notification
          if (!showingFailureNotification) {
            setSnackbarMessage("Document Failed to Download");
          }
          setLastFailedOperation('download');
          setLastFailedDocument(targetDocument);
          return;
        }
        
        signedUrl = signedUrlData.url;
        fileName = signedUrlData.key.split('/').pop() || 
          `${targetDocument.clientName}_${targetDocument.name}.pdf`;
      } else {
        return;
      }

      // Handle download based on whether we need to use the API or direct fetch
      let blob: Blob;
      
      if (directUrl) {
        // Direct fetch for PDF viewer downloads
        const response = await fetch(signedUrl);
        blob = await response.blob();
      } else {
        // API-based download for menu downloads
        const accessToken = await getTokenSilently();
        const response = await axios.post(
          `${apiRequest}/${ApiCalls.DownloadBloomDocument}`,
          { fileUrl: signedUrl, fileName },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            responseType: 'arraybuffer',
            cancelToken: cancelTokenRef.current?.token,
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setDownloadProgress(percentCompleted);
                setSnackbarMessage(`Document Downloading...`);
              }
            },
          }
        );

        if (response.data instanceof ArrayBuffer) {
          const uint8Array = new Uint8Array(response.data);
          const firstBytes = String.fromCharCode.apply(null, Array.from(uint8Array.slice(0, 4)));
          
          if (firstBytes === '%PDF') {
            blob = new Blob([response.data], { type: 'application/pdf' });
          } else {
            const textDecoder = new TextDecoder();
            const base64String = textDecoder.decode(response.data);
            try {
              const binaryString = atob(base64String);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              blob = new Blob([bytes], { type: 'application/pdf' });
            } catch {
              blob = new Blob([response.data], { type: 'application/pdf' });
            }
          }
        } else {
          blob = new Blob([response.data], { type: 'application/pdf' });
        }
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      setDownloadedFileUrl(blobUrl);
      
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS || isSafari) {
        const file = new File([blob], fileName, { type: 'application/pdf' });
        
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file] });
            setDownloadStatus('completed');
            setSnackbarMessage("Document saved successfully!");
            return;
          } catch (shareErr: any) {
            if (shareErr?.name === 'AbortError') {
              setDownloadStatus('idle');
              setSnackbarOpen(false);
              return;
            }
          }
        }
        
        window.open(blobUrl, '_blank');
        setDownloadStatus('completed');
        setSnackbarMessage("Document opened - use Share button to save");
      } else {
        saveAs(blob, fileName);
        setDownloadStatus('completed');
        setSnackbarMessage("Document Successfully Downloaded!");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return;
      }
      setDownloadStatus('error');
      // Only show snackbar if we're not already showing a failure notification
      if (!showingFailureNotification) {
        setSnackbarMessage("Document Failed to Download");
      }
      setLastFailedOperation('download');
      setLastFailedDocument(targetDocument);
    } finally {
      setActionLoading(false);
      setDownloadProgress(0);
    }
  };

  const handleView = async (document?: Document) => {
    const docToView = document || selectedDocument;
    
    if (!docToView) {
      handleMenuClose();
      return;
    }

    handleMenuClose();
    
    const fileName = `${docToView.clientName}_${docToView.name}.pdf`;
    setPdfFileName(fileName);
    setPdfUrl("");
    setPdfViewerOpen(true);
    setGlobalPdfViewerOpen(true);
    setActionLoading(true);
    
    try {
      const signedUrlData = await fetchSignedUrl(docToView);
      if (signedUrlData?.url) {
        const actualFileName = signedUrlData.key.split('/').pop() || fileName;
        
        setPdfUrl(signedUrlData.url);
        setPdfFileName(actualFileName);
      } else {
        setDownloadStatus('error');
        // Only show snackbar if we're not already showing a failure notification
        if (!showingFailureNotification) {
          setSnackbarMessage("Failed to fetch document");
          setSnackbarOpen(true);
        }
        setPdfViewerOpen(false);
        setGlobalPdfViewerOpen(false);
        setLastFailedOperation('view');
        setLastFailedDocument(docToView);
      }
    } catch (err) {
      setDownloadStatus('error');
      // Only show snackbar if we're not already showing a failure notification
      if (!showingFailureNotification) {
        setSnackbarMessage("Failed to fetch document");
        setSnackbarOpen(true);
      }
      setPdfViewerOpen(false);
      setGlobalPdfViewerOpen(false);
      setLastFailedOperation('view');
      setLastFailedDocument(docToView);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    setGlobalPdfViewerOpen(false);
    setPdfUrl("");
    setPdfFileName("");
  };

  const handleShare = async (directUrl?: string, directFileName?: string, document?: Document) => {
    // Determine which document to use
    const targetDocument = document || selectedDocument;
    
    if (!targetDocument && !directUrl) {
      handleMenuClose();
      return;
    }

    handleMenuClose();
    setActionLoading(true);
    setDownloadStatus('sharing');
    setSnackbarMessage("Preparing to share...");
    setSnackbarOpen(true);

    try {
      let signedUrl: string;
      let fileName: string;

      // If direct URL provided (from PDF viewer), use it
      if (directUrl && directFileName) {
        signedUrl = directUrl;
        fileName = directFileName;
      } else if (targetDocument) {
        // Fetch signed URL for menu-based shares
        const signedUrlData = await fetchSignedUrl(targetDocument);
        
        if (!signedUrlData?.url) {
          setDownloadStatus('error');
          // Only show snackbar if we're not already showing a failure notification
          if (!showingFailureNotification) {
            setSnackbarMessage("Failed to prepare document for sharing");
          }
          setLastFailedOperation('share');
          setLastFailedDocument(targetDocument);
          return;
        }
        
        signedUrl = signedUrlData.url;
        fileName = signedUrlData.key.split('/').pop() || 
          `${targetDocument.clientName}_${targetDocument.name}.pdf`;
      } else {
        return;
      }
      
      const response = await fetch(signedUrl);
      const blob = await response.blob();
      const file = new File([blob], fileName, { type: 'application/pdf' });
      
      setSnackbarOpen(false);
      
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
          });
          setDownloadStatus('completed');
          setSnackbarMessage("Document shared successfully!");
          setSnackbarOpen(true);
          setTimeout(() => setSnackbarOpen(false), 2000);
          return;
        } catch (shareErr: any) {
          if (shareErr?.name === 'AbortError') {
            setActionLoading(false);
            return;
          }
        }
      }
      
      await navigator.clipboard.writeText(signedUrl);
      setDownloadStatus('completed');
      setSnackbarMessage("Link copied to clipboard!");
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 2000);
      
    } catch (err) {
      setDownloadStatus('error');
      // Only show snackbar if we're not already showing a failure notification
      if (!showingFailureNotification) {
        setSnackbarMessage("Document Failed to Share");
        setSnackbarOpen(true);
      }
      setLastFailedOperation('share');
      setLastFailedDocument(targetDocument);
    } finally {
      setActionLoading(false);
    }
  };



  const handleRetry = () => {
    if (!lastFailedDocument || !lastFailedOperation) return;
    
    const doc = lastFailedDocument;
    const operation = lastFailedOperation;
    
    console.log('[DocumentViewer] Retrying operation:', operation, 'for document:', doc);
    
    setSnackbarOpen(false);
    setDownloadStatus('idle');
    setLastFailedOperation(null);
    setLastFailedDocument(null);
    
    if (operation === 'download') {
      console.log('[DocumentViewer] Calling handleDownload for retry');
      setTimeout(() => handleDownload(undefined, undefined, doc), 100);
    } else if (operation === 'share') {
      console.log('[DocumentViewer] Calling handleShare for retry');
      setTimeout(() => handleShare(undefined, undefined, doc), 100);
    } else if (operation === 'view') {
      console.log('[DocumentViewer] Calling handleView for retry with document:', doc);
      // Pass the document directly to handleView
      setTimeout(() => handleView(doc), 100);
    }
  };



  const handleClientFilter = (clientId: number | null) => {
    setSelectedClientId(clientId);
    setDocumentTypeFilter("");
  };

  const handleDocumentTypeChange = (event: SelectChangeEvent<string>) => {
    setDocumentTypeFilter(event.target.value);
  };

  // Get document types based on selected client
  const getAvailableDocumentTypes = () => {
    if (selectedClientId === null) {
      return [...new Set(documents.map((doc) => doc.name))];
    } else {
      return [...new Set(
        documents
          .filter((doc) => doc.clientId === selectedClientId)
          .map((doc) => doc.name)
      )];
    }
  };

  const documentTypes = getAvailableDocumentTypes();

  const filteredDocuments = documents.filter((doc) => {
    const matchesClient = selectedClientId === null || doc.clientId === selectedClientId;
    const matchesType = !documentTypeFilter || doc.name === documentTypeFilter;
    return matchesClient && matchesType;
  });

  // Determine which documents to display
  const displayDocuments = maxDisplayDocuments 
    ? filteredDocuments.slice(0, maxDisplayDocuments)
    : filteredDocuments;

  return (
    <div className={isAllDocuments ? "all-documents-container" : "client-documents-container"}>
      {showBreadcrumbs && (
        <Breadcrumbs className="breadcrumbs" aria-label="breadcrumb">
          <Link
            underline="hover"
            color="text.secondary"
            onClick={() => navigate("/appointments")}
            sx={{ cursor: "pointer" }}
          >
            Home
          </Link>
          <Typography color="text.primary">See All Documents</Typography>
        </Breadcrumbs>
      )}

      {!isAllDocuments && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6" component="h2">
            Documents
          </Typography>
          {maxDisplayDocuments &&
            documents.length > maxDisplayDocuments &&
            onSeeAllDocuments && (
              <Button onClick={onSeeAllDocuments} sx={{ minHeight: 44 }}>
                See all documents
              </Button>
            )}
        </Stack>
      )}

      {isAllDocuments && clients.length > 0 && (
        <div className="client-filter-buttons">
          {clients.map((client) => (
            <Button
              key={client.id}
              variant={selectedClientId === client.id ? "contained" : "outlined"}
              onClick={() =>
                handleClientFilter(selectedClientId === client.id ? null : client.id)
              }
              className={`client-filter-btn ${
                selectedClientId === client.id ? "active" : ""
              }`}
            >
              {client.name}
            </Button>
          ))}
        </div>
      )}

      {isAllDocuments && (
        <FormControl fullWidth className="document-type-filter">
          <InputLabel id="document-type-label">Select Document Type</InputLabel>
          <Select
            labelId="document-type-label"
            id="document-type-select"
            value={documentTypes.includes(documentTypeFilter) ? documentTypeFilter : ""}
            label="Select Document Type"
            onChange={handleDocumentTypeChange}
          >
            <MenuItem value="">
              <em>All Types</em>
            </MenuItem>
            {documentTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {loading && !isAllDocuments ? (
        <Stack spacing={1.5} role="status" aria-busy="true" aria-label="Loading documents">
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      ) : loading ? (
        <div className="no-documents">
          <p>Loading documents...</p>
        </div>
      ) : error ? (
        <div className="no-documents" style={{color: "#666", fontSize: "14px"}}>
          <p>Unable to load documents at this time.</p>
        </div>
      ) : displayDocuments.length === 0 ? (
        <div className="no-documents">
          <p>No documents available{isAllDocuments ? "." : " at this time."}.</p>
        </div>
      ) : (
        <>
          <div className="documents-list">
            {displayDocuments.map((document) => {
              const isFailedDocument = failedDocuments.has(document.id);
              
              return (
                <div 
                  key={document.id} 
                  className={`document-card ${isFailedDocument ? 'clickable' : ''}`}
                  onClick={isFailedDocument ? () => handleFailedDocumentClick(document) : undefined}
                >
                  <Avatar
                    className="document-avatar"
                    sx={{
                      bgcolor: isFailedDocument ? "#f5f5f5" : "#e8f0fc",
                      width: 40,
                      height: 40,
                    }}
                  >
                    <ContentPasteIcon sx={{ color: isFailedDocument ? "#9e9e9e" : "#10389a" }} />
                  </Avatar>
                  <div className="document-info">
                    <span className="document-name">
                      {document.clientName} - {document.name}
                    </span>
                    {isFailedDocument ? (
                      <span className="document-error" style={{color: "#666", fontSize: "14px"}}>
                        Unable to load document
                      </span>
                    ) : (
                      document.date && (
                        <span className="document-date">
                          {new Date(document.date).toLocaleDateString()}
                        </span>
                      )
                    )}
                  </div>
                  {isFailedDocument ? (
                    <IconButton
                      className="document-warning-icon"
                      aria-label="document warning"
                      disabled
                    >
                      <ErrorOutlineIcon sx={{ color: "#d32f2f" }} />
                    </IconButton>
                  ) : (
                    <IconButton
                      className="document-menu-button"
                      onClick={(e) => handleMenuOpen(e, document)}
                      aria-label="document options"
                      sx={{ width: 44, height: 44 }}
                      disabled={actionLoading}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </div>
              );
            })}
          </div>

        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleDownload()} disabled={actionLoading}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{actionLoading ? "Loading..." : "Download Document"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleView()} disabled={actionLoading}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{actionLoading ? "Loading..." : "View Document"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleShare()} disabled={actionLoading}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{actionLoading ? "Loading..." : "Share Document"}</ListItemText>
        </MenuItem>
      </Menu>

      <Modal
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        aria-labelledby="pdf-viewer-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {pdfUrl && (
                <>
                  <IconButton 
                    onClick={() => handleDownload(pdfUrl, pdfFileName)}
                    title="Download Document"
                    sx={{ color: "#1976D2" }}
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleShare(pdfUrl, pdfFileName)}
                    title="Share Document"
                    sx={{ color: "#1976D2" }}
                  >
                    <ShareIcon />
                  </IconButton>
                </>
              )}
              <IconButton onClick={handleClosePdfViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", height: "calc(100% - 60px)" }}>
            {pdfUrl ? (
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <div style={{ height: "100%" }}>
                  <Viewer fileUrl={pdfUrl} />
                </div>
              </Worker>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                Fetching requested documents...
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen && !showingFailureNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => downloadStatus !== 'downloading' && downloadStatus !== 'viewing' && downloadStatus !== 'sharing' && downloadStatus !== 'error' && setSnackbarOpen(false)}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: downloadStatus === 'completed' 
              ? '#E8F5E9' 
              : downloadStatus === 'error' 
                ? '#FFEBEE' 
                : '#E3F2FD',
            borderRadius: '8px',
            padding: '12px 16px',
            width: '90vw',
            boxShadow: '0px 2px 12px 0px rgba(18, 47, 78, 0.16)',
          }}
        >
          {downloadStatus === 'downloading' ? (
            <DownloadIcon sx={{ color: '#1976D2' }} />
          ) : downloadStatus === 'viewing' ? (
            <VisibilityIcon sx={{ color: '#1976D2' }} />
          ) : downloadStatus === 'sharing' ? (
            <ShareIcon sx={{ color: '#1976D2' }} />
          ) : downloadStatus === 'completed' ? (
            <CheckCircleOutlineIcon sx={{ color: '#2E7D32' }} />
          ) : downloadStatus === 'error' ? (
            <ErrorOutlineIcon sx={{ color: '#C62828' }} />
          ) : null}
          
          <Box sx={{ flex: 1 }}>
            <span style={{ 
              color: downloadStatus === 'completed' 
                ? '#1B5E20' 
                : downloadStatus === 'error' 
                  ? '#B71C1C' 
                  : '#1565C0',
              fontWeight: 500,
              fontSize: '14px'
            }}>
              {snackbarMessage}
            </span>
          </Box>
          
          {downloadStatus === 'downloading' && (
            <Button
              size="small"
              onClick={handleCancelDownload}
              sx={{ 
                color: '#1976D2', 
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
              }}
            >
              Cancel
            </Button>
          )}
          
          {downloadStatus === 'completed' && downloadedFileUrl && (
            <Button
              size="small"
              onClick={handleOpenDownloadedFile}
              sx={{ 
                color: '#2E7D32', 
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' }
              }}
            >
              Open
            </Button>
          )}
          
          {downloadStatus === 'error' && lastFailedOperation && (
            <Button
              size="small"
              onClick={handleRetry}
              sx={{ 
                color: '#C62828', 
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(198, 40, 40, 0.08)' }
              }}
            >
              Retry
            </Button>
          )}


          <IconButton onClick={() => setSnackbarOpen(false)}>
              <CloseIcon  />
          </IconButton>          
          
          {downloadStatus === 'cancelled' && (
            <IconButton size="small" onClick={() => setSnackbarOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Snackbar>

      {/* Failure Notification */}
      {failureNotification.show && (
        <Box
          className="failure-notification"
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            width: '90%',
            maxWidth: '600px',
            backgroundColor: failureNotification.isDeepStorage ? '#E8F5E9' : '#FFEBEE',
            borderRadius: '8px',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.15)',
            border: failureNotification.isDeepStorage ? '1px solid #C8E6C9' : '1px solid #FFCDD2',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ErrorOutlineIcon sx={{ 
              color: failureNotification.isDeepStorage ? '#2E7D32' : '#C62828', 
              fontSize: 20 
            }} />
            
            <Box sx={{ flex: 1 }}>
              <span style={{ 
                color: failureNotification.isDeepStorage ? '#1B5E20' : '#B71C1C',
                fontWeight: 500,
                fontSize: '14px'
              }}>
                {failureNotification.message}
              </span>
            </Box>
            
            <Button
              size="small"
              onClick={handleNotificationRetry}
              sx={{ 
                color: failureNotification.isDeepStorage ? '#2E7D32' : '#C62828', 
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { 
                  backgroundColor: failureNotification.isDeepStorage 
                    ? 'rgba(46, 125, 50, 0.08)' 
                    : 'rgba(198, 40, 40, 0.08)' 
                }
              }}
            >
              Retry
            </Button>

            <IconButton 
              size="small" 
              onClick={hideFailureNotification}
              sx={{ color: failureNotification.isDeepStorage ? '#2E7D32' : '#C62828' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {/* Progress bar */}
          <Box
            sx={{
              width: '100%',
              height: '2px',
              backgroundColor: failureNotification.isDeepStorage ? '#C8E6C9' : '#FFCDD2',
              borderRadius: '1px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${failureNotification.progress}%`,
                height: '100%',
                backgroundColor: failureNotification.isDeepStorage ? '#2E7D32' : '#C62828',
                transition: 'width 0.3s ease-out',
              }}
            />
          </Box>
        </Box>
      )}
    </div>
  );
};

export default DocumentViewer;